import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, ChartWrapper, EmptyState } from './index.styles';
import { BarSeriesChart } from './BarSeriesChart';
import { AreaSeriesChart } from './AreaSeriesChart';
import ProfileIcons from '../../../ProfileIcons';
import { useContainerHeight } from '../useContainerHeight';

/**
 * Transforms the new API series format into a flat array suitable for Recharts.
 * Each item in the result has { date, [metric1]: value1, [metric2]: value2, ... }
 */
const transformSeriesToChartData = (series: TimeSeriesSeries[]): Array<Record<string, unknown> & { date: string }> => {
  const dateMap = new Map<string, Record<string, unknown>>();

  series.forEach((s) => {
    s.items.forEach((item) => {
      const existing = dateMap.get(item.date) ?? { date: item.date };

      existing[s.metric] = item.value;
      dateMap.set(item.date, existing);
    });
  });

  return Array.from(dateMap.values()).sort((a, b) => String(a.date).localeCompare(String(b.date))) as Array<
    Record<string, unknown> & { date: string }
  >;
};

export const TimeSeriesRenderer = ({
  data,
  selectedProfiles,
  supportedPlatforms,
}: DynamicModuleBuilderProps & {
  selectedProfiles?: ReportProfile[];
  supportedPlatforms?: string[];
}) => {
  const { t } = useTranslation('reports');
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const chartHeight = useContainerHeight(chartWrapperRef as React.RefObject<HTMLDivElement>);

  if (!data || (data.chartType !== 'Line' && data.chartType !== 'Bar' && data.chartType !== 'Area')) {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const { chartType, metrics, data: rawData, options } = data as TimeSeriesModuleData;

  if (!rawData?.series || rawData.series.length === 0) {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const chartData = transformSeriesToChartData(rawData.series);

  if (chartData.length === 0) {
    return <EmptyState>{t('new_reports_no_performance_data')}</EmptyState>;
  }

  const isStacked = options?.stacked ?? false;

  const renderChart = () => {
    if (chartType === 'Bar') {
      return (
        <BarSeriesChart
          chartData={chartData}
          metrics={metrics}
          isStacked={isStacked}
          chartHeight={chartHeight}
        />
      );
    }

    return (
      <AreaSeriesChart
        chartData={chartData}
        metrics={metrics}
        chartHeight={chartHeight}
      />
    );
  };

  return (
    <Container>
      <ChartWrapper ref={chartWrapperRef}>{renderChart()}</ChartWrapper>
      <ProfileIcons
        isChartType
        selectedProfiles={selectedProfiles}
        supportedPlatforms={supportedPlatforms}
      />
    </Container>
  );
};
